<template>
  <section v-if="visualVideos.length" class="ni-section ni-visual" id="visual-qingdao">
    <div class="ni-container ni-container--wide">
      <SectionHeader
        variant="dark"
        :subtitle="t('newIndex.sections.visual.subtitle')"
        more-href="/topic_video"
      >
        <template #title>
          <span class="ni-section-header__title-mark">
            {{ t('newIndex.sections.visual.title') }}
          </span>
        </template>
      </SectionHeader>

      <div class="ni-visual__mobile-list">
        <button
          v-for="(video, idx) in visualVideos"
          :id="`mobile-visual-card-${idx}`"
          :key="`mobile-${video.id}`"
          type="button"
          class="ni-visual__mobile-card"
          @click="goVideoDetail(video.id)"
        >
          <div class="ni-visual__mobile-card-media">
            <img :src="video.imageUrl" :alt="video.title" loading="lazy" />
            <span class="ni-visual__mobile-card-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            <div class="ni-visual__mobile-card-vignette"></div>
            <h3 class="ni-visual__mobile-card-title">{{ video.title }}</h3>
          </div>
        </button>
      </div>

      <div
        class="ni-visual__body"
        :title="t('newIndex.sections.visual.scrollHint')"
        @wheel.prevent="handleWheel"
      >
        <div
          class="ni-visual__stage"
          @mouseenter="isStageHovered = true"
          @mouseleave="isStageHovered = false"
          @click="goVideoDetail(displayVideo.id)"
        >
          <div class="ni-visual__stage-inner">
            <Transition name="ni-visual-fade" mode="out-in">
              <video
                v-if="isPlaying && displayVideo.videoUrl"
                ref="stageVideoRef"
                :key="`video-${displayVideo.id}`"
                class="ni-visual__stage-video"
                :src="displayVideo.videoUrl"
                :poster="displayVideo.imageUrl"
                autoplay
                muted
                playsinline
                controls
                controlslist="nodownload"
                oncontextmenu="return false"
                @play="handleVideoPlay"
                @pause="handleVideoPause"
              />
              <img
                v-else
                :key="displayVideo.id"
                :src="displayVideo.imageUrl"
                :alt="displayVideo.title"
              />
            </Transition>
            <div class="ni-visual__stage-vignette"></div>
          </div>

          <div
            class="ni-visual__play"
            :class="{ 'ni-visual__play--visible': showPlayOverlay }"
          >
            <button
              id="visual-center-play-button"
              type="button"
              class="ni-visual__play-btn"
              :class="{ 'ni-visual__play-btn--playing': isPlaying && !isVideoPaused }"
              @click.stop="togglePlay"
            >
              <svg
                v-if="isPlaying && !isVideoPaused"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="currentColor"
                aria-hidden="true"
              >
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="34" height="34" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          <div
            class="ni-visual__hud"
            :class="{ 'ni-visual__hud--visible': showHud }"
          >
            <Transition name="ni-visual-fade" mode="out-in">
              <h3 :key="displayVideo.id" class="ni-visual__hud-title">{{ displayVideo.title }}</h3>
            </Transition>
          </div>
        </div>

        <div class="ni-visual__dock" @mouseleave="handleDockLeave">
          <div class="ni-visual__pills">
            <button
              v-for="(video, idx) in visualVideos"
              :id="`visual-video-pill-${idx}`"
              :key="video.id"
              type="button"
              class="ni-visual__pill"
              :class="{ 'ni-visual__pill--active': idx === expandedIdx }"
              @mouseenter="handlePillEnter(idx)"
              @click="handleSelectVideo(idx)"
            >
              <div class="ni-visual__pill-thumb">
                <img :src="video.imageUrl" :alt="video.title" />
                <span class="ni-visual__pill-play" aria-hidden="true">
                  <span class="ni-visual__pill-play-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </span>
              </div>
              <span class="ni-visual__pill-title">{{ video.title }}</span>
            </button>
          </div>

          <div class="ni-visual__rail">
            <button
              type="button"
              class="ni-visual__rail-arrow"
              :aria-label="t('newIndex.sections.visual.prev')"
              @click="handlePrev"
            >
              ▲
            </button>
            <div class="ni-visual__rail-dots">
              <button
                v-for="(video, idx) in visualVideos"
                :key="`dot-${video.id}`"
                type="button"
                class="ni-visual__rail-dot"
                :class="{ 'ni-visual__rail-dot--active': idx === expandedIdx }"
                :aria-label="video.title"
                @click="handleSelectVideo(idx)"
              />
            </div>
            <button
              type="button"
              class="ni-visual__rail-arrow"
              :aria-label="t('newIndex.sections.visual.next')"
              @click="handleNext"
            >
              ▼
            </button>
          </div>
        </div>
      </div>

      <div class="ni-visual__mobile-tabs">
        <button
          v-for="(video, idx) in visualVideos"
          :id="`mobile-visual-pill-tab-${idx}`"
          :key="video.id"
          type="button"
          class="ni-visual__mobile-tab"
          :class="{ 'ni-visual__mobile-tab--active': idx === activeVideoIdx }"
          @click="handleSelectVideo(idx)"
        >
          {{ video.title }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SectionHeader from './shared/SectionHeader.vue';

const props = defineProps({
  videos: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const router = useRouter();
const stageVideoRef = ref(null);
const activeVideoIdx = ref(0);
const hoveredIdx = ref(null);
const isPlaying = ref(false);
const isVideoPaused = ref(true);
const isStageHovered = ref(false);
const lastScrollTime = ref(0);

const visualVideos = computed(() => (props.videos || []).slice(0, 5));

const expandedIdx = computed(() => (
  hoveredIdx.value !== null ? hoveredIdx.value : activeVideoIdx.value
));

const displayVideo = computed(() => {
  const list = visualVideos.value;
  if (!list.length) {
    return { id: '', title: '', imageUrl: '', videoUrl: '' };
  }
  return list[expandedIdx.value] || list[0];
});

const showPlayOverlay = computed(() => {
  if (!isPlaying.value) return true;
  return isVideoPaused.value || isStageHovered.value;
});

const showHud = computed(() => !isStageHovered.value);

function handleVideoPlay() {
  isVideoPaused.value = false;
}

function handleVideoPause() {
  isVideoPaused.value = true;
}

function resetVideoState() {
  isPlaying.value = false;
  isVideoPaused.value = true;
  isStageHovered.value = false;
}

function handlePillEnter(idx) {
  hoveredIdx.value = idx;
}

function handleDockLeave() {
  hoveredIdx.value = null;
}

function handleSelectVideo(idx) {
  activeVideoIdx.value = idx;
  resetVideoState();
}

function togglePlay() {
  if (!displayVideo.value.videoUrl) return;

  if (!isPlaying.value) {
    isPlaying.value = true;
    isVideoPaused.value = true;
    return;
  }

  const video = stageVideoRef.value;
  if (!video) return;

  if (video.paused) {
    video.play();
  } else {
    video.pause();
  }
}

function goVideoDetail(id) {
  if (!id) return;
  router.push({ path: '/topic_video_detail', query: { id } });
}

function handlePrev() {
  const len = visualVideos.value.length;
  if (!len) return;
  activeVideoIdx.value = (activeVideoIdx.value - 1 + len) % len;
  resetVideoState();
}

function handleNext() {
  const len = visualVideos.value.length;
  if (!len) return;
  activeVideoIdx.value = (activeVideoIdx.value + 1) % len;
  resetVideoState();
}

function handleWheel(event) {
  const now = Date.now();
  if (now - lastScrollTime.value < 500) return;

  if (Math.abs(event.deltaY) > 8) {
    lastScrollTime.value = now;
    if (event.deltaY > 0) {
      handleNext();
    } else {
      handlePrev();
    }
  }
}

watch(visualVideos, (list) => {
  if (activeVideoIdx.value >= list.length) {
    activeVideoIdx.value = 0;
  }
});
</script>
